import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import myAxios from '../AxiosUrl';
import { toast, ToastContainer } from 'react-toastify';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import { AiOutlineMail, AiOutlineEye, AiOutlineCheckCircle, AiOutlineInbox, AiOutlineSearch, AiOutlineDelete } from 'react-icons/ai';
import { BiMessageDetail } from 'react-icons/bi';
import styles from './ContactMessages.module.css';

function ContactMessages() {
  const uid = localStorage.getItem('id');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [spamCount, setSpamCount] = useState(0);
  // the stat tiles always show the unfiltered (non-spam) numbers
  const [allTotal, setAllTotal] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const limit = 20;

  const fetchMessages = () => {
    setLoading(true);
    const skip = (currentPage - 1) * limit;
    let url = `/contact-messages/${uid}?limit=${limit}&skip=${skip}`;
    if (statusFilter) url += `&status=${statusFilter}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;

    myAxios.get(url)
      .then(res => {
        if (res.data.success) {
          setMessages(res.data.data.messages);
          setTotal(res.data.data.total);
          setUnreadCount(res.data.data.unreadCount);
          setSpamCount(res.data.data.spamCount || 0);
          if (!statusFilter) setAllTotal(res.data.data.total);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load messages');
        setLoading(false);
      });
  };

  useEffect(() => { fetchMessages(); }, [currentPage, statusFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchMessages();
  };

  const handleUpdateStatus = (messageId, status) => {
    myAxios.put(`/contact-message/${uid}`, { messageId, status })
      .then(res => {
        if (res.data.success) {
          toast.success('Status updated');
          fetchMessages();
          if (selectedMessage && selectedMessage._id === messageId) {
            setSelectedMessage({ ...selectedMessage, status });
          }
        }
      })
      .catch(() => toast.error('Failed to update'));
  };

  const handleDelete = (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    myAxios.delete(`/contact-message/${uid}/${messageId}`)
      .then(res => {
        if (res.data.success) {
          toast.success('Message deleted');
          setShowModal(false);
          fetchMessages();
        }
      })
      .catch(() => toast.error('Failed to delete'));
  };

  const handleUpdateNotes = (messageId, adminNotes) => {
    myAxios.put(`/contact-message/${uid}`, { messageId, adminNotes })
      .then(res => {
        if (res.data.success) {
          toast.success('Notes saved');
          fetchMessages();
        }
      })
      .catch(() => toast.error('Failed to save notes'));
  };

  const openMessage = (msg) => {
    setSelectedMessage(msg);
    setShowModal(true);
    if (msg.status === 'unread') {
      handleUpdateStatus(msg._id, 'read');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'unread': return { bg: '#fee2e2', color: '#dc2626' };
      case 'read': return { bg: '#dbeafe', color: '#2563eb' };
      case 'replied': return { bg: '#dcfce7', color: '#16a34a' };
      case 'archived': return { bg: '#f3f4f6', color: '#6b7280' };
      case 'spam': return { bg: '#fef3c7', color: '#92400e' };
      default: return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };

  const statusFilters = [
    { label: 'All', value: '' },
    { label: 'Unread', value: 'unread' },
    { label: 'Read', value: 'read' },
    { label: 'Replied', value: 'replied' },
    { label: 'Archived', value: 'archived' },
    // bot messages (fake phone, money promise, link…) — kept out of the list and counts
    { label: spamCount ? `Spam (${spamCount})` : 'Spam', value: 'spam' },
  ];

  const totalPages = Math.ceil(total / limit);

  return (
    <div className={styles['shopify-container']}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Page Header */}
      <div className={styles['page-header']}>
        <div className={styles['page-title-section']}>
          <h1 className={styles['page-title']}>Contact Messages</h1>
          <p className={styles['page-subtitle']}>Manage customer inquiries and contact form submissions</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles['shopify-stats-section']}>
        <div className={styles['stats-cards']}>
          <div className={styles['stat-card']}>
            <div className={styles['stat-icon']}>
              <BiMessageDetail />
            </div>
            <div className={styles['stat-content']}>
              <div className={styles['stat-value']}>{allTotal ?? total}</div>
              <div className={styles['stat-label']}>Total Messages</div>
            </div>
          </div>
          <div className={styles['stat-card']}>
            <div className={styles['stat-icon-red']}>
              <AiOutlineMail />
            </div>
            <div className={styles['stat-content']}>
              <div className={styles['stat-value']}>{unreadCount}</div>
              <div className={styles['stat-label']}>Unread</div>
            </div>
          </div>
          <div className={styles['stat-card']}>
            <div className={styles['stat-icon-blue']}>
              <AiOutlineEye />
            </div>
            <div className={styles['stat-content']}>
              <div className={styles['stat-value']}>{Math.max((allTotal ?? total) - unreadCount, 0)}</div>
              <div className={styles['stat-label']}>Read / Replied</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className={styles['filters-section']}>
        {/* Status Filter Chips */}
        <div className={styles['shopify-status-filters']}>
          <div className={styles['filter-chips']}>
            {statusFilters.map(f => (
              <span
                key={f.value}
                className={`${styles['filter-chip']} ${statusFilter === f.value ? styles['filter-chip-active'] : ''}`}
                onClick={() => { setStatusFilter(f.value); setCurrentPage(1); }}
              >
                {f.label}
              </span>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className={styles['shopify-search-section']}>
          <form className={styles['search-form']} onSubmit={handleSearchSubmit}>
            <input
              type="text"
              placeholder="Search by name, email, phone, or message..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles['shopify-search-input']}
            />
            <button type="submit" className={styles['shopify-search-submit']}>
              <AiOutlineSearch /> Search
            </button>
          </form>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className={styles.skeletonContainer} style={{padding: 0, overflow: 'hidden'}}>
          <div className={styles.skeletonTableHeader}>
            {['Name', 'Contact', 'Subject', 'Message', 'Status', 'Date', 'Actions'].map((_, i) => (
              <div key={i} className={styles.skeleton} style={{height: '14px', borderRadius: '4px'}}></div>
            ))}
          </div>
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className={styles.skeletonTableRow}>
              <div className={styles.skeleton} style={{height: '16px', borderRadius: '4px'}}></div>
              <div>
                <div className={styles.skeleton} style={{height: '14px', width: '80%', marginBottom: '4px', borderRadius: '4px'}}></div>
                <div className={styles.skeleton} style={{height: '12px', width: '60%', borderRadius: '4px'}}></div>
              </div>
              <div className={styles.skeleton} style={{height: '14px', borderRadius: '4px'}}></div>
              <div className={styles.skeleton} style={{height: '14px', borderRadius: '4px'}}></div>
              <div className={styles.skeleton} style={{width: '65px', height: '24px', borderRadius: '20px'}}></div>
              <div className={styles.skeleton} style={{height: '14px', borderRadius: '4px'}}></div>
              <div className={styles.skeleton} style={{width: '70px', height: '30px', borderRadius: '6px'}}></div>
            </div>
          ))}
        </div>
      ) : messages.length === 0 ? (
        <div className={styles['empty-section']}>No messages found</div>
      ) : (
        <>
          <div className={styles['table-section']}>
            <TableContainer component={Paper} className={styles['shopify-table-wrapper']}>
              <Table className={styles['shopify-table']}>
                <TableHead className={styles['table-header']}>
                  <TableRow>
                    <TableCell className={styles['header-cell']}>Name</TableCell>
                    <TableCell className={styles['header-cell']}>Contact</TableCell>
                    <TableCell className={styles['header-cell']}>Subject</TableCell>
                    <TableCell className={styles['header-cell']}>Message</TableCell>
                    <TableCell className={styles['header-cell']}>Status</TableCell>
                    <TableCell className={styles['header-cell']}>Date</TableCell>
                    <TableCell className={styles['header-cell']}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messages.map((msg) => {
                    const statusStyle = getStatusColor(msg.status);
                    return (
                      <TableRow
                        key={msg._id}
                        className={`${styles['table-row']} ${msg.status === 'unread' ? styles['unread-row'] : ''}`}
                        onClick={() => openMessage(msg)}
                      >
                        <TableCell className={styles['table-cell']}>
                          <span className={styles['customer-name']}>{msg.name}</span>
                        </TableCell>
                        <TableCell className={styles['table-cell']}>
                          {msg.email && <div className={styles['customer-contact']}>{msg.email}</div>}
                          {msg.phone && <div className={styles['customer-contact']}>{msg.phone}</div>}
                        </TableCell>
                        <TableCell className={styles['table-cell']}>{msg.subject || '-'}</TableCell>
                        <TableCell className={styles['table-cell']}>
                          <div className={styles['message-truncate']}>
                            {msg.message.length > 60 ? msg.message.substring(0, 60) + '...' : msg.message}
                          </div>
                        </TableCell>
                        <TableCell className={styles['table-cell']}>
                          <span className={styles['status-badge']} style={{ background: statusStyle.bg, color: statusStyle.color }}>
                            {msg.status}
                          </span>
                        </TableCell>
                        <TableCell className={styles['table-cell']}>
                          <span className={styles['date-cell']}>{formatDate(msg.createdAt)}</span>
                        </TableCell>
                        <TableCell className={styles['table-cell']}>
                          <button className={styles['action-btn-danger']} onClick={(e) => { e.stopPropagation(); handleDelete(msg._id); }}>
                            <AiOutlineDelete /> Delete
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {totalPages > 1 && (
            <div className={styles['pagination-section']}>
              <Pagination count={totalPages} page={currentPage} onChange={(e, p) => setCurrentPage(p)} color="primary" />
            </div>
          )}
        </>
      )}

      {/* Message Detail Modal — rendered via portal to escape layout overflow */}
      {showModal && selectedMessage && ReactDOM.createPortal(
        <div className={styles['modal-overlay']} onClick={() => setShowModal(false)}>
          <div className={styles['modal']} onClick={(e) => e.stopPropagation()}>
            <div className={styles['modal-header']}>
              <h3>Message from {selectedMessage.name}</h3>
              <button className={styles['modal-close']} onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className={styles['modal-body']}>
              <div className={styles['modal-info']}>
                {selectedMessage.email && <p><strong>Email:</strong> <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a></p>}
                {selectedMessage.phone && <p><strong>Phone:</strong> <a href={`tel:${selectedMessage.phone}`}>{selectedMessage.phone}</a></p>}
                {selectedMessage.subject && <p><strong>Subject:</strong> {selectedMessage.subject}</p>}
                <p><strong>Date:</strong> {formatDate(selectedMessage.createdAt)}</p>
              </div>
              <div className={styles['modal-message']}>
                <strong>Message:</strong>
                <p>{selectedMessage.message}</p>
              </div>
              <div className={styles['modal-actions']}>
                <label>Status:</label>
                <select
                  value={selectedMessage.status}
                  onChange={(e) => {
                    handleUpdateStatus(selectedMessage._id, e.target.value);
                    setSelectedMessage({ ...selectedMessage, status: e.target.value });
                  }}
                  className={styles['shopify-select']}
                >
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                  <option value="replied">Replied</option>
                  <option value="archived">Archived</option>
                  <option value="spam">Spam</option>
                </select>
              </div>
              <div className={styles['modal-notes']}>
                <label>Admin Notes:</label>
                <textarea
                  defaultValue={selectedMessage.adminNotes || ''}
                  placeholder="Add internal notes..."
                  rows={3}
                  className={styles['notes-textarea']}
                  id="adminNotesInput"
                />
                <button
                  className={styles['save-notes-btn']}
                  onClick={() => {
                    const notes = document.getElementById('adminNotesInput').value;
                    handleUpdateNotes(selectedMessage._id, notes);
                  }}
                >
                  Save Notes
                </button>
              </div>
            </div>
            <div className={styles['modal-footer']}>
              <button className={styles['action-btn-danger']} onClick={() => handleDelete(selectedMessage._id)}>
                <AiOutlineDelete /> Delete Message
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default ContactMessages;
